import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import type { Color } from "@/lib/types";
import { avatarColor, cn, initials } from "@/lib/utils";

type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";

export const Button = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant; size?: "sm" | "md"; loading?: boolean; icon?: React.ComponentType<{ className?: string }> }
>(function Button({ variant = "secondary", size = "md", loading, icon: Icon, className, children, disabled, type = "button", ...rest }, ref) {
  const variants: Record<ButtonVariant, string> = {
    primary: "bg-accent text-white hover:bg-accent-hover",
    secondary: "border border-line text-fg hover:bg-hover",
    ghost: "text-muted hover:bg-hover hover:text-fg",
    danger: "bg-danger text-white hover:brightness-110",
  };
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={cn(
        "inline-flex items-center justify-center gap-1.5 rounded-md font-medium whitespace-nowrap transition-colors disabled:opacity-50",
        size === "sm" ? "h-7 px-2 text-[13px]" : "h-8 px-3 text-sm",
        variants[variant],
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner className="size-3.5" /> : Icon && <Icon className="size-4 shrink-0" />}
      {children}
    </button>
  );
});

export function Spinner({ className }: { className?: string }) {
  return <span className={cn("inline-block size-5 animate-spin rounded-full border-2 border-current border-t-transparent opacity-60", className)} />;
}

export function PageSpinner() {
  return (
    <div className="flex h-full min-h-40 items-center justify-center text-muted">
      <Spinner />
    </div>
  );
}

export function Tag({ color, children, className, onRemove }: { color: Color; children: ReactNode; className?: string; onRemove?: () => void }) {
  return (
    <span className={cn("tag", `tag-${color}`, className)} dir="auto">
      <span className="truncate">{children}</span>
      {onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="ms-1 opacity-60 hover:opacity-100"
          aria-label="Remove"
        >
          ×
        </button>
      )}
    </span>
  );
}

export function StageBadge({ color, children }: { color: Color; children: ReactNode }) {
  return (
    <span className={cn("tag rounded-full ps-1.5 pe-2", `tag-${color}`)}>
      <span className={cn("me-1.5 inline-block size-2 shrink-0 rounded-full", `dot-${color}`)} />
      <span className="truncate">{children}</span>
    </span>
  );
}

export function Avatar({ id, name, size = 20, className }: { id: string; name: string; size?: number; className?: string }) {
  return (
    <span
      title={name}
      className={cn("inline-flex shrink-0 items-center justify-center rounded-full font-medium text-white select-none", className)}
      style={{ width: size, height: size, fontSize: Math.max(9, size * 0.48), background: avatarColor(id) }}
    >
      {initials(name)}
    </span>
  );
}

export function UserChip({ id, name, size = 20, className }: { id: string; name: string; size?: number; className?: string }) {
  return (
    <span className={cn("inline-flex min-w-0 items-center gap-1.5", className)}>
      <Avatar id={id} name={name} size={size} />
      <span className="truncate" dir="auto">
        {name}
      </span>
    </span>
  );
}

export function EmptyState({ icon: Icon, title, children, action }: { icon?: React.ComponentType<{ className?: string }>; title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-line px-6 py-10 text-center">
      {Icon && <Icon className="size-8 text-faint" />}
      <div className="font-medium">{title}</div>
      {children && <div className="max-w-sm text-sm text-muted">{children}</div>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="flex flex-col items-center gap-3 py-16 text-center">
      <div className="text-muted">{message}</div>
      {onRetry && (
        <Button size="sm" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}

export function Field({ label, hint, children, error }: { label: string; hint?: ReactNode; children: ReactNode; error?: string }) {
  return (
    <label className="block space-y-1">
      <span className="text-[13px] font-medium text-muted">{label}</span>
      {children}
      {hint && !error && <span className="block text-xs text-faint">{hint}</span>}
      {error && <span className="block text-xs text-danger">{error}</span>}
    </label>
  );
}

export function ProgressBar({ done, total, className }: { done: number; total: number; className?: string }) {
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <div className={cn("flex items-center gap-2", className)} title={`${done} of ${total} tasks done`}>
      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-active">
        <div className="h-full rounded-full bg-[var(--dot-green)] transition-all" style={{ width: `${pct}%` }} />
      </div>
      <span className="w-9 text-end text-xs text-muted tabular-nums">{pct}%</span>
    </div>
  );
}

export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="rounded border border-line px-1 font-sans text-[11px] text-muted">{children}</kbd>;
}
